import { createStore, type StoreApi } from 'zustand/vanilla';
import { createDispatcher, type Dispatch } from '@/core/app/dispatch';
import { UndoManager } from '@/core/app/undo';
import { appVersion } from '@/app/config/features';
import { requestPersistentStorage } from '@/app/persistStorage';
import { claimSaveOwnership } from '@/app/tabLock';
import { getContent } from '@/core/content/data';
import type { ContentRegistry } from '@/core/content/registry';
import { createNewSave } from '@/core/domain/newSave';
import { DOMAIN_SYSTEMS } from '@/core/domain/systems';
import { EventBus } from '@/core/events/bus';
import { decodeSaveFile, encodeSaveFile, type SaveFileResult } from '@/core/persistence/saveFile';
import { LctDb, type LedgerEntry } from '@/core/persistence/db';
import { SaveRepository, StaleWriteError } from '@/core/persistence/SaveRepository';
import { PhotoRepository } from '@/core/persistence/PhotoRepository';
import { MusicLibrary, type MusicAddResult, type MusicTrackInfo } from '@/core/persistence/MusicLibrary';
import type { PhotoRecord } from '@/core/persistence/db';
import { SaveScheduler } from '@/core/persistence/SaveScheduler';
import { AVATAR_NAME_MAX, type GameSave } from '@/core/persistence/schema/v1';
import { getPlatform, type PlatformServices } from '@/platform';

export type { GameSave, MusicTrackInfo, PhotoRecord };
export { AVATAR_NAME_MAX };

/** Everything the UI and the Pixi game share. Built once at boot. */
export interface GameServices {
  content: ContentRegistry;
  store: StoreApi<GameSave>;
  dispatch: Dispatch;
  bus: EventBus;
  saves: SaveScheduler;
  /** Start over: stops saving, backs up and clears the save. The caller reloads the page afterwards. */
  resetGame: () => Promise<void>;
  /** Export the save as an `lct-save` file (Settings → Xuất file lưu, content-schemas §11.7). */
  exportSave: () => string;
  /** Read an exported file without touching anything (size, format, migration, schema) + a summary. */
  readSaveFile: (text: string) => SaveFileResult;
  /** Replace the save with a file the player confirmed: back up the current one, write it, then reload. */
  importSave: (save: GameSave) => Promise<void>;
  /** Album photo pixels (IndexedDB); `takePhoto` / `deletePhoto` keep the save's id list in step. */
  photos: {
    put: (photo: PhotoRecord) => Promise<void>;
    get: (id: string) => Promise<PhotoRecord | undefined>;
    list: () => Promise<PhotoRecord[]>;
    /** Only for a photo whose takePhoto failed; Album deletes go through the deletePhoto command. */
    remove: (id: string) => Promise<void>;
  };
  /** "Nhạc trong máy": the player's own tracks, on this device only. Changes reach the music player at once. */
  music: {
    list: () => Promise<MusicTrackInfo[]>;
    add: (files: readonly File[]) => Promise<MusicAddResult>;
    remove: (id: string) => Promise<void>;
  };
  /** Organizing commands go through here so they can be undone (content-schemas §13.4). */
  undo: UndoManager;
  platform: PlatformServices;
  /** How the save was obtained, for a one-off toast. */
  loadKind: 'new' | 'ok' | 'recovered';
  /** Another tab took the game over: this one stopped saving and must show "đang mở ở tab khác". */
  onSaveOwnershipLost: (fn: () => void) => () => void;
}

export type BootResult =
  | { kind: 'ready'; services: GameServices }
  | { kind: 'corrupt'; rawJson: string; recovery: SaveRecovery }
  | { kind: 'future'; version: number }
  /** Another tab owns the save (content-schemas §11.2). */
  | { kind: 'otherTab' };

/** What the "save is broken" screen can do (content-schemas §11.3). Each one ends with a page reload. */
export interface SaveRecovery {
  readSaveFile: (text: string) => SaveFileResult;
  importSave: (save: GameSave) => Promise<void>;
  /** Start over; the broken save is kept as a `pre-reset` backup. */
  reset: () => Promise<void>;
}

function randomSeed(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] ?? 1;
}

/**
 * Boot (content-schemas §11.3): load the save (or recover / start fresh), wire the dispatcher to the store,
 * schedule saves and flush them whenever the app is hidden. Never resets a broken save silently.
 */
let booting: Promise<BootResult> | undefined;

/**
 * Boot once per page. React StrictMode (and dev hot reloads of the shell) mount the shell twice; a second boot
 * would start a second save writer and the two would trip each other's stale-write guard.
 */
export function bootServices(): Promise<BootResult> {
  booting ??= boot();
  return booting;
}

async function boot(): Promise<BootResult> {
  const content = getContent();
  const platform = getPlatform();
  const db = new LctDb();
  const repo = new SaveRepository(db, {
    backupEveryRevisions: content.balance.save.backupEveryRevisions,
    backupMinIntervalMs: content.balance.save.backupMinIntervalMs,
    backupKeep: content.balance.save.backupKeep,
  });

  // One tab owns the save; a second one shows "đang mở ở tab khác" instead of loading it.
  const owner = await claimSaveOwnership();
  if (!owner) return { kind: 'otherTab' };

  const loaded = await repo.load();
  if (loaded.kind === 'corrupt')
    return {
      kind: 'corrupt',
      rawJson: loaded.rawJson,
      recovery: {
        readSaveFile: decodeSaveFile,
        importSave: (save) => repo.importSave(save),
        reset: () => repo.reset(),
      },
    };
  if (loaded.kind === 'future') return { kind: 'future', version: loaded.version };

  if (loaded.kind !== 'new') requestPersistentStorage();

  const initial =
    loaded.kind === 'new'
      ? createNewSave(content, { saveId: crypto.randomUUID(), rngSeed: randomSeed(), nowWallMs: Date.now() })
      : loaded.save;

  const store = createStore<GameSave>(() => initial);
  const bus = new EventBus();

  // Ledger rows wait here until the next save writes them in the same IndexedDB transaction.
  let pendingLedger: LedgerEntry[] = [];
  const saves = new SaveScheduler({
    debounceMs: content.balance.save.debounceMs,
    criticalMs: content.balance.save.criticalMs,
    write: async () => {
      const ledger = pendingLedger;
      pendingLedger = [];
      const written = await repo.write(store.getState(), ledger).catch((e: unknown) => {
        pendingLedger = [...ledger, ...pendingLedger];
        throw e;
      });
      // Keep changes made while writing; only adopt the new revision.
      store.setState((s) => ({ ...s, revision: written.revision, updatedAt: written.updatedAt }));
    },
    onError: (error) => {
      if (error instanceof StaleWriteError) console.warn('[save] another tab wrote a newer save');
      else console.error('[save] write failed', error);
    },
  });

  const dispatch = createDispatcher({
    content,
    getState: () => store.getState(),
    setState: (next) => {
      store.setState(next, true);
    },
    emit: (events) => {
      bus.emitAll(events);
    },
    markDirty: (priority) => {
      saves.markDirty(priority);
    },
    now: () => Date.now(),
    // Fixed order (content-schemas §13.3): stats → coziness → … (friendship, achievements, suggestions, unlocks).
    systems: DOMAIN_SYSTEMS,
    onLedger: (entries) => {
      pendingLedger.push(...entries);
    },
  });

  // Handing the game to another tab: write what we have, then never write again.
  owner.beforeRelease(async () => {
    await saves.flush();
    saves.dispose();
  });
  owner.onLost(() => {
    saves.dispose();
  });

  // Reconcile derived state (coziness, content version) with the current content on every boot.
  dispatch({ type: 'bootSession', nowWallMs: Date.now() }, 'system');
  if (loaded.kind === 'new') saves.markDirty('critical');
  platform.lifecycle.onPause(() => {
    void saves.flush();
  });

  // Photos dropped from the Album (deleted, or replaced when full) lose their pixels too.
  const photoRepo = new PhotoRepository(db);
  bus.onAny((e) => {
    if (e.type === 'PHOTO_DELETED') void photoRepo.delete(e.photoId);
  });

  const undo = new UndoManager(dispatch, () => store.getState(), content.balance.undoMaxDepth);

  const musicLibrary = new MusicLibrary(db, () => crypto.randomUUID(), Date.now);
  const pushTracks = async () => {
    platform.music.setDeviceTracks(await musicLibrary.tracks());
  };
  void pushTracks().catch(() => undefined);

  return {
    kind: 'ready',
    services: {
      content,
      store,
      dispatch,
      bus,
      saves,
      undo,
      platform,
      loadKind: loaded.kind,
      onSaveOwnershipLost: (fn) => owner.onLost(fn),
      music: {
        list: () => musicLibrary.list(),
        add: async (files) => {
          const r = await musicLibrary.add(files);
          await pushTracks();
          return r;
        },
        remove: async (id) => {
          await musicLibrary.remove(id);
          await pushTracks();
        },
      },
      photos: {
        put: (photo) => photoRepo.put(photo),
        get: (id) => photoRepo.get(id),
        list: () => photoRepo.list(store.getState().saveId),
        remove: (id) => photoRepo.delete(id),
      },
      resetGame: async () => {
        saves.dispose();
        await repo.reset();
      },
      exportSave: () => encodeSaveFile(store.getState(), Date.now(), appVersion),
      readSaveFile: decodeSaveFile,
      importSave: async (save) => {
        await saves.flush();
        saves.dispose();
        await repo.importSave(save);
      },
    },
  };
}
